import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  get #singleOpenContext() {
    const parent = this.closest('.accordion');
    return (parent && parent.hasAttribute('data-single-open')) || this.closest('.product-information .product-details') !== null;
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * When in product information single-open context, closes other accordions in the same group when this one opens,
   * and scrolls the opened accordion heading into view.
   */
  get #scrollIntoViewContext() {
    const parent = this.closest('.accordion');
    return (parent && parent.hasAttribute('data-scroll-into-view')) || this.closest('.product-information .product-details') !== null;
  }

  /**
   * When in product information single-open context, closes other accordions in the same group when this one opens,
   * and scrolls the opened accordion heading into view.
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    if (this.#singleOpenContext) {
      const accordionGroup = this.closest('.accordion');
      if (accordionGroup) {
        const siblings = accordionGroup.querySelectorAll('accordion-custom');
        for (const sibling of siblings) {
          if (sibling !== this && sibling instanceof AccordionCustom && sibling.details) {
            sibling.details.open = false;
          }
        }
      }
    }

    // Image accordion: switch the active image panel
    this.#handleImageSwitch();

    if (this.#scrollIntoViewContext) {
      // Scroll the accordion heading into view so it stays visible when content is long (offset for sticky header)
      const headerOffset = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() || '67px';
      requestAnimationFrame(() => {
        this.summary.style.scrollMarginTop = headerOffset;
        this.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /**
   * Handles switching the active image in an accordion-with-image section.
   * Reads the data-image-target attribute from this accordion row and activates the matching image panel.
   */
  #handleImageSwitch() {
    const accordionGroup = this.closest('[data-image-accordion]');
    if (!accordionGroup) return;

    const section = this.closest('.accordion-with-image');
    if (!section) return;

    // If default media is set, skip switching — the default panel stays active permanently
    const wrapper = section.querySelector('[data-image-wrapper]');
    if (wrapper && wrapper.hasAttribute('data-default-media')) return;

    const targetId = this.dataset.imageTarget;
    if (!targetId) return;

    const allPanels = section.querySelectorAll('.accordion-with-image__image-panel');
    for (const panel of allPanels) {
      panel.classList.remove('accordion-with-image__image-panel--active');
      // Pause any video in deactivated panels
      const video = panel.querySelector('video');
      if (video) video.pause();
    }

    const targetPanel = document.getElementById(targetId);
    if (targetPanel) {
      targetPanel.classList.add('accordion-with-image__image-panel--active');
      // Play video in the active panel
      const video = targetPanel.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
